import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * AdhdFocusSection
 * Implements the Nordic Editorial Clinical / Calming Wellness design specification
 * for ADHD-behandling in Copenhagen with geometric pastel graphics and pill CTAs.
 * All texts are fully CMS-editable via the 'adhd_fokus_sektion' collection.
 */
export default function AdhdFocusSection({ lang: propLang, data: initialData }) {
  const { lang: ctxLang, getLocalizedValue, localizePath } = useLanguage();
  const lang = propLang || ctxLang || 'da';
  const isDa = lang === 'da';

  const [cmsData, setCmsData] = useState(initialData || null);

  useEffect(() => {
    if (initialData) {
      setCmsData(initialData);
      return;
    }
    let isMounted = true;
    const fetchCmsData = async () => {
      try {
        let res = await fetch('/_api/public/content/adhd_fokus_sektion');
        if (!res.ok) {
          res = await fetch('https://api.dev.auxo.dk/public/content/adhd_fokus_sektion');
        }
        if (res.ok && isMounted) {
          const json = await res.json();
          if (Array.isArray(json) && json.length > 0) {
            setCmsData(json[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching adhd_fokus_sektion:', err);
      }
    };
    fetchCmsData();
    return () => {
      isMounted = false;
    };
  }, [initialData]);

  const getText = (field, fallback) => {
    if (!cmsData || !cmsData[field]) return fallback;
    const val = getLocalizedValue(cmsData[field]);
    return val || fallback;
  };

  const renderTitle = (val) => {
    if (!val) return null;
    if (typeof val === 'string' && val.includes('\n')) {
      return val.split('\n').map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < val.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
    return val;
  };

  const badgeText = getText('badge', isDa ? 'Vores Særlige Fokus' : 'Our Special Focus');
  const headlineText = getText('overskrift', isDa ? 'ADHD-behandling i København' : 'ADHD Treatment in Copenhagen');
  const descriptionText = getText(
    'beskrivelse',
    isDa
      ? 'ADHD-behandling ligger os særligt på sinde. Vi ser ofte, hvor stor forskel den rette behandling kan gøre.'
      : 'ADHD treatment is especially close to our hearts. We often see what a tremendous difference the right treatment can make.'
  );

  const col1Title = getText('kolonne_1_titel', isDa ? 'Medicinsk behandling af ADHD' : 'Medical Treatment of ADHD');
  const col1Text = getText(
    'kolonne_1_tekst',
    isDa
      ? 'Vi vurderer, om medicin er relevant for dig, og står for opstart, dosisjustering og tæt opfølgning.'
      : 'We evaluate whether medication is relevant for you and manage initiation, dosage adjustment, and close follow-up.'
  );

  const col2Title = getText('kolonne_2_titel', isDa ? 'Psykoedukation om ADHD' : 'Psychoeducation on ADHD');
  const col2Text = getText(
    'kolonne_2_tekst',
    isDa
      ? 'Viden om ADHD gør det lettere at forstå dine udfordringer og styrker. Vi gennemgår opmærksomhed og relationer.'
      : 'Knowledge about ADHD makes it easier to understand your challenges and strengths. We cover attention, executive function, and relationships.'
  );

  const col3Title = getText('kolonne_3_titel', isDa ? 'Psykoterapi ved ADHD' : 'Psychotherapy for ADHD');
  const col3Text = getText(
    'kolonne_3_tekst',
    isDa
      ? 'Samtaleforløb med fokus på de udfordringer, der ofte følger med ADHD, fx stress, lavt selvværd og angst.'
      : 'Therapeutic consultations focusing on challenges that often accompany ADHD, such as stress, low self-esteem, and anxiety.'
  );

  const bottomText = getText(
    'bundtekst',
    isDa
      ? 'Behandlingen er for unge og voksne, både hvis du er udredt hos os, og hvis du allerede har fået stillet diagnosen et andet sted.'
      : 'Treatment is for adolescents and adults, both if you were assessed with us or if you were previously diagnosed elsewhere.'
  );

  const btn1Text = getText('knap_1_tekst', isDa ? 'Start dit forløb hos os' : 'Start your treatment with us');
  const btn1Link = cmsData?.knap_1_link || '/kontakt';

  const btn2Text = getText('knap_2_tekst', isDa ? 'Læs om medicinsk behandling' : 'Read about medical treatment');
  const btn2Link = cmsData?.knap_2_link || '/medicinsk-behandling';

  return (
    <div className="w-full bg-[#f9f9f5] border border-[#e6e6de] rounded-3xl p-6 sm:p-10 md:p-14 shadow-none">
      
      {/* Header Section */}
      <header className="text-center max-w-2xl mx-auto space-y-3">
        <p className="text-xs uppercase tracking-[0.25em] text-[#606c66] font-medium font-sans">
          {badgeText}
        </p>

        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-[#173831] tracking-tight font-normal leading-tight">
          {headlineText}
        </h2>
        
        {/* Caret Divider */}
        <div className="flex items-center justify-center gap-3 pt-1 text-[#5e6662]/60">
          <span className="h-px w-16 bg-[#e6e6de]" />
          <svg className="w-3.5 h-3.5 stroke-[1.5]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="m18 15-6-6-6 6" />
          </svg>
          <span className="h-px w-16 bg-[#e6e6de]" />
        </div>

        <p className="text-[#5e6662] text-sm md:text-base leading-relaxed pt-1 font-sans">
          {descriptionText}
        </p>
      </header>

      {/* 3-Column Features */}
      <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#e6e6de]">
        
        {/* Col 1: Medicinsk behandling */}
        <div className="flex flex-col items-center text-center px-4 sm:px-6 py-8 md:py-2">
          <div className="relative w-36 h-28 mb-6 flex items-center justify-center">
            <span className="absolute w-20 h-20 rounded-full bg-[#ccd7c5]/80 -translate-x-3 mix-blend-multiply" />
            <span className="absolute w-20 h-20 rounded-full bg-[#cfd6e7]/80 translate-x-3 mix-blend-multiply" />
          </div>
          <h3 className="font-display text-2xl text-[#173831] mb-3 leading-snug font-normal">
            {renderTitle(col1Title)}
          </h3>
          <p className="text-[#5e6662] text-sm leading-relaxed font-sans max-w-xs">
            {col1Text}
          </p>
        </div>

        {/* Col 2: Psykoedukation */}
        <div className="flex flex-col items-center text-center px-4 sm:px-6 py-8 md:py-2">
          <div className="relative w-36 h-28 mb-6 flex items-center justify-center">
            <span className="absolute w-20 h-20 bg-[#eedec8]/80 -translate-x-2.5 [clip-path:polygon(50%_0%,0%_100%,100%_100%)] mix-blend-multiply" />
            <span className="absolute w-20 h-20 bg-[#c6cfbd]/80 translate-x-2.5 translate-y-1 [clip-path:polygon(50%_0%,0%_100%,100%_100%)] mix-blend-multiply" />
          </div>
          <h3 className="font-display text-2xl text-[#173831] mb-3 leading-snug font-normal">
            {renderTitle(col2Title)}
          </h3>
          <p className="text-[#5e6662] text-sm leading-relaxed font-sans max-w-xs">
            {col2Text}
          </p>
        </div>

        {/* Col 3: Psykoterapi */}
        <div className="flex flex-col items-center text-center px-4 sm:px-6 py-8 md:py-2">
          <div className="relative w-36 h-28 mb-6 flex items-center justify-center">
            <span className="absolute w-18 h-18 rounded-2xl bg-[#cfd6e7]/80 -translate-x-2 -translate-y-2 mix-blend-multiply" />
            <span className="absolute w-18 h-18 rounded-2xl bg-[#eee0cc]/80 translate-x-2 translate-y-2 mix-blend-multiply" />
          </div>
          <h3 className="font-display text-2xl text-[#173831] mb-3 leading-snug font-normal">
            {renderTitle(col3Title)}
          </h3>
          <p className="text-[#5e6662] text-sm leading-relaxed font-sans max-w-xs">
            {col3Text}
          </p>
        </div>

      </div>

      {/* Divider Line */}
      <hr className="border-t border-[#e6e6de] my-10" />

      {/* Bottom Notice & CTAs */}
      <div className="text-center max-w-2xl mx-auto space-y-7">
        <p className="text-[#5e6662] text-sm leading-relaxed font-sans">
          {bottomText}
        </p>

        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link
            to={localizePath(btn1Link)}
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#173831] text-white text-sm font-medium hover:bg-[#102924] transition-colors shadow-xs"
          >
            <span>{btn1Text}</span>
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </Link>
          <Link
            to={localizePath(btn2Link)}
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#173831] text-[#173831] text-sm font-medium hover:bg-[#173831]/5 transition-colors"
          >
            <span>{btn2Text}</span>
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </Link>
        </div>
      </div>

    </div>
  );
}
